import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { KpiCard } from "@/components/StatusBadge";
import { STATUS_LABELS, brl, fmtPct, statusColor, monthLabel, CONTATO_FORMAS, CONTATO_RESPONSAVEIS } from "@/lib/format";
import { TrendingUp, TrendingDown, Calendar, ArrowRight, EyeOff, RotateCcw, Coffee, GraduationCap, CalendarClock, Phone, X, CheckCircle2, Pencil, Trash2, CheckSquare, Clock } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { CompleteMeetingTaskModal, CompleteTreinamentoTaskModal } from "@/components/ActivityModals";

export default function Dashboard() {
    const [data, setData] = useState(null);
    const [alerts, setAlerts] = useState([]);
    const [kpisExtra, setKpisExtra] = useState(null);
    const [loading, setLoading] = useState(true);
    const [listModal, setListModal] = useState(null); // M2 — {title, items, emptyText}
    const [contatarOpen, setContatarOpen] = useState(false); // M4 — modal clientes a contatar
    const navigate = useNavigate();

    async function loadDashboard() {
        const [s, a, k] = await Promise.all([
            api.get("/dashboard/summary"),
            api.get("/alerts-ext"),
            api.get("/dashboard/kpis-extra"),
        ]);
        setData(s.data); setAlerts(a.data); setKpisExtra(k.data);
    }

    useEffect(() => {
        loadDashboard().finally(() => setLoading(false));
    }, []);

    if (loading || !data || !kpisExtra) return <div className="text-sm text-slate-500">Carregando dashboard…</div>;

    const statusEntries = Object.entries(data.by_status);
    const totalStatus = statusEntries.reduce((s, [, v]) => s + v, 0) || 1;
    const pieData = statusEntries.map(([k, v]) => ({
        name: STATUS_LABELS[k], value: v, color: statusColor(k), key: k,
        pct: Math.round((v / totalStatus) * 100),
    }));

    const renderPieLabel = (entry) => {
        const { cx, cy, midAngle, innerRadius, outerRadius, payload } = entry;
        const value = payload?.value ?? entry.value;
        if (!value) return null;
        const pct = payload?.pct ?? Math.round((value / totalStatus) * 100);
        const RADIAN = Math.PI / 180;
        const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);
        return (
            <text x={x} y={y} fill="#FFFFFF" textAnchor="middle" dominantBaseline="central" style={{ fontSize: 14, fontWeight: 800, fontFamily: "'Work Sans', sans-serif" }}>
                {pct}%
            </text>
        );
    };

    return (
        <div className="space-y-8 fade-in" data-testid="dashboard-page">
            <header>
                <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-medium">Customer Success</div>
                <h1 className="font-display font-bold text-3xl text-[#0F2044] mt-1">Dashboard</h1>
                <p className="text-sm text-slate-500 mt-1">Visão consolidada da saúde dos contratos e ações prioritárias do dia.</p>
            </header>

            {/* KPIs — M15: clicáveis, encaminham para a página correspondente */}
            <section className="grid grid-cols-3 gap-4" data-testid="kpi-row">
                <KpiCard testid="kpi-contracts" label="Contratos ativos" value={data.total_contratos}
                    hint="Ver todos os contratos"
                    onClick={() => navigate("/contratos")} />
                <KpiCard testid="kpi-interacoes"
                    label={`Interações em ${monthLabel(data.mes_referencia) || "—"}`}
                    value={data.interacoes_mes.toLocaleString("pt-BR")}
                    accent={data.variacao_interacoes != null ? {
                        label: fmtPct(data.variacao_interacoes, { signed: true }),
                        color: data.variacao_interacoes >= 0 ? "#22C55E" : "#EF4444",
                    } : null}
                    hint={`vs ${monthLabel(data.mes_referencia_anterior) || "mês anterior"}: ${data.interacoes_mes_anterior.toLocaleString("pt-BR")} · só meses encerrados`}
                    onClick={() => navigate("/relatorios")}
                />
                <KpiCard testid="kpi-criticos" label="Críticos / Atenção"
                    value={`${data.by_status.critico} / ${data.by_status.atencao}`}
                    hint={`Saudáveis: ${data.by_status.saudavel} · Excelentes: ${data.by_status.excelente}`}
                    onClick={() => navigate("/contratos?filter=criticos_atencao")}
                />
            </section>

            {/* KPIs operacionais (clicáveis) */}
            <section className="grid grid-cols-3 gap-4" data-testid="kpi-extra-row">
                <ClickableKpi
                    icon={CalendarClock} color="#EF4444"
                    label="Contratos próximos do vencimento" value={kpisExtra.vencimento_60d.count}
                    hint="Vencem em até 60 dias"
                    onClick={() => navigate("/contratos?filter=vencimento_60d")}
                    testid="kpi-vencimento" />
                <ClickableKpi
                    icon={Phone} color="#0EA5E9"
                    label="Clientes a contatar" value={kpisExtra.a_contatar_mes.count}
                    hint="Sem reunião/treinamento há 45d (Premium) ou 30d (Personalizado)"
                    onClick={() => setContatarOpen(true)}
                    testid="kpi-a-contatar" />
                <ClickableKpi
                    icon={EyeOff} color="#94A3B8"
                    label="Clientes sem interação no prazo" value={kpisExtra.sem_interacao.count}
                    hint={`Limite: ${kpisExtra.sem_interacao.threshold_days} dias (editável em Configurações)`}
                    onClick={() => navigate("/contratos?filter=sem_interacao")}
                    testid="kpi-sem-interacao" />
                <ClickableKpi
                    icon={RotateCcw} color="#10B981"
                    label="Renovações nos próximos 60 dias" value={kpisExtra.renovacao_60d.count}
                    hint="Encaminhar comerciais e reunião de check"
                    onClick={() => navigate("/contratos?filter=renovacao_60d")}
                    testid="kpi-renovacao" />
                <ClickableKpi
                    icon={Coffee} color="#3B82F6"
                    label="Reuniões realizadas no mês" value={kpisExtra.reunioes_mes}
                    hint={`${kpisExtra.reunioes_total ?? 0} no total · em todos os contratos`}
                    onClick={() => setListModal({
                        title: "Reuniões realizadas no mês",
                        items: kpisExtra.reunioes_mes_detalhe || [],
                        emptyText: "Nenhuma reunião registrada no mês corrente.",
                    })}
                    testid="kpi-reunioes" />
                <ClickableKpi
                    icon={GraduationCap} color="#8B5CF6"
                    label="Treinamentos realizados no mês" value={kpisExtra.treinamentos_mes}
                    hint={`${kpisExtra.treinamentos_total ?? 0} no total · inclui checks da jornada`}
                    onClick={() => setListModal({
                        title: "Treinamentos realizados no mês",
                        items: kpisExtra.treinamentos_mes_detalhe || [],
                        emptyText: "Nenhum treinamento registrado no mês corrente (registros manuais e checks da jornada).",
                    })}
                    testid="kpi-treinamentos" />
            </section>

            {listModal && <ListModal {...listModal} onClose={() => setListModal(null)} />}
            {contatarOpen && <AContatarModal items={kpisExtra.a_contatar_detalhe || []} onClose={() => setContatarOpen(false)} onRegistered={loadDashboard} />}

            <section className="grid grid-cols-3 gap-6">
                {/* Alerts panel */}
                <div className="col-span-2 bg-white border border-slate-200 rounded-lg p-6" data-testid="alerts-panel">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">Alertas prioritários</div>
                            <h2 className="font-display font-semibold text-lg text-[#0F2044]">Ações que demandam atenção imediata</h2>
                        </div>
                    </div>
                    {alerts.length === 0 ? (
                        <div className="text-sm text-slate-500 py-6 text-center">Nenhum alerta no momento. Tudo sob controle.</div>
                    ) : (
                        <ul className="divide-y divide-slate-100">
                            {alerts.slice(0, 8).map((a, i) => (
                                <li key={i} className="py-3 flex items-start gap-3" data-testid={`alert-row-${i}`}>
                                    <div className={`mt-1 h-2 w-2 rounded-full ${a.nivel === "critico" ? "bg-[#EF4444]" : "bg-[#F59E0B]"}`} />
                                    <div className="flex-1 min-w-0">
                                        <Link to={`/contratos/${a.contract_id}`} className="font-medium text-[#0F2044] hover:underline text-sm">
                                            {a.organizacao_nome}
                                        </Link>
                                        <div className="text-sm text-slate-700">{a.gatilho}</div>
                                        <div className="text-xs text-slate-500">Ação sugerida: {a.acao}</div>
                                    </div>
                                    <Link to={`/contratos/${a.contract_id}`} className="text-xs text-[#0F2044] hover:underline whitespace-nowrap flex items-center gap-1" data-testid={`alert-goto-${i}`}>
                                        Acessar <ArrowRight className="h-3 w-3" />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Pie chart distribution — M5: clicável (fatia + legenda) → filtra contratos por status */}
                <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="score-distribution">
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">Distribuição por status</div>
                    <h2 className="font-display font-semibold text-lg text-[#0F2044]">Saúde dos contratos</h2>
                    <p className="text-[11px] text-slate-400 mt-0.5">Clique em uma fatia ou item da legenda para ver os contratos.</p>
                    <div className="h-56 mt-3" style={{ minWidth: 0 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    dataKey="value"
                                    nameKey="name"
                                    innerRadius={46}
                                    outerRadius={88}
                                    paddingAngle={2}
                                    labelLine={false}
                                    label={({ payload, percent }) => {
                                        const p = payload?.pct ?? Math.round((percent || 0) * 100);
                                        return `${p}%`;
                                    }}
                                    isAnimationActive={false}
                                    onClick={(d) => { const k = d?.key || d?.payload?.key; const v = d?.value ?? d?.payload?.value; if (k && v > 0) navigate(`/contratos?filter=status_${k}`); }}
                                    style={{ cursor: "pointer" }}
                                >
                                    {pieData.map((p) => <Cell key={p.key} fill={p.color} stroke="#FFFFFF" strokeWidth={2} style={{ cursor: "pointer" }} />)}
                                </Pie>
                                <Tooltip formatter={(v, n, item) => [`${v} contrato${v !== 1 ? "s" : ""} · ${item.payload.pct}%`, n]} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <ul className="mt-3 space-y-1.5" data-testid="status-legend">
                        {pieData.map(p => (
                            <li key={p.key}>
                                <button
                                    onClick={() => p.value > 0 && navigate(`/contratos?filter=status_${p.key}`)}
                                    disabled={p.value === 0}
                                    data-testid={`legend-item-${p.key}`}
                                    className={`w-full flex items-center justify-between text-xs rounded px-1.5 py-1 -mx-1.5 transition-colors ${p.value > 0 ? "hover:bg-slate-50 cursor-pointer" : "cursor-default"}`}>
                                    <span className="flex items-center gap-2 text-slate-700">
                                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: p.color }} />
                                        <span className="font-medium">{p.name}</span>
                                    </span>
                                    <span className="font-mono-tab text-slate-700">
                                        <strong className="text-[#0F2044]">{p.value}</strong> <span className="text-slate-400">·</span> {p.pct}%
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section className="grid grid-cols-2 gap-6">
                <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="top-growth">
                    <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="h-4 w-4 text-[#22C55E]" />
                        <h3 className="font-display font-semibold text-base text-[#0F2044]">Top 5 — Crescimento</h3>
                    </div>
                    <Movers items={data.top_growth} positive />
                </div>
                <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="top-decline">
                    <div className="flex items-center gap-2 mb-3">
                        <TrendingDown className="h-4 w-4 text-[#EF4444]" />
                        <h3 className="font-display font-semibold text-base text-[#0F2044]">Top 5 — Queda</h3>
                    </div>
                    <Movers items={data.top_decline} positive={false} />
                </div>
            </section>

            <AgendaPanel agenda={data.agenda} onChanged={loadDashboard} />
        </div>
    );
}

// M17 — Agenda dos próximos 7 dias com ações diretas e seleção múltipla
function AgendaPanel({ agenda, onChanged }) {
    const [selected, setSelected] = useState(new Set());
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(null);
    const [remanejando, setRemanejando] = useState(null);
    const [activityModal, setActivityModal] = useState(null); // item 1 — {type, contract, task}
    const rows = agenda.slice(0, 20);

    function toggle(id) {
        setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
    }
    function toggleAll() {
        setSelected(prev => prev.size === rows.length ? new Set() : new Set(rows.map(r => r.task_id)));
    }
    async function concluir(a) {
        // Item 1 — reunião/treinamento não concluem direto: abrem o modal respectivo
        if (a.activity_type === "treinamento" || a.activity_type === "reuniao" || a.is_meeting) {
            setActivityModal({
                type: a.activity_type === "treinamento" ? "treinamento" : "reuniao",
                contract: { id: a.contract_id, organizacao_nome: a.organizacao_nome },
                task: { id: a.task_id, descricao: a.descricao, predicted_date: a.predicted_date, override_date: a.predicted_date },
            });
            return;
        }
        setBusy(true);
        try { await api.patch(`/tasks/${a.task_id}`, { status: "concluido", observacao: "Concluída pela Agenda do Dashboard" }); await onChanged(); }
        finally { setBusy(false); }
    }
    async function excluir(a) {
        if (!window.confirm(`Excluir a tarefa "${a.descricao}"?`)) return;
        setBusy(true);
        try { await api.delete(`/tasks/${a.task_id}`); await onChanged(); }
        finally { setBusy(false); }
    }
    async function salvarRemanejo() {
        if (!remanejando?.date) return;
        setBusy(true);
        try { await api.patch(`/tasks/${remanejando.task.task_id}`, { override_date: remanejando.date }); setRemanejando(null); await onChanged(); }
        finally { setBusy(false); }
    }
    async function salvarEdicao() {
        if (!editing?.descricao?.trim()) return;
        setBusy(true);
        try {
            await api.patch(`/tasks/${editing.task_id}`, { descricao: editing.descricao.trim(), responsavel: editing.responsavel });
            setEditing(null); await onChanged();
        } finally { setBusy(false); }
    }
    async function bulkConcluir() {
        if (selected.size === 0) return;
        // Item 1 — reuniões/treinamentos exigem formulário: são ignoradas no lote
        const byId = Object.fromEntries(rows.map(r => [r.task_id, r]));
        const excluir = Array.from(selected).filter(id => {
            const r = byId[id];
            return r && (r.activity_type === "treinamento" || r.activity_type === "reuniao" || r.is_meeting);
        });
        const comuns = Array.from(selected).filter(id => !excluir.includes(id));
        if (comuns.length === 0) {
            window.alert("As tarefas selecionadas são Reuniões/Treinamentos e precisam ser concluídas individualmente (com formulário). Nenhuma tarefa comum para concluir em lote.");
            return;
        }
        let msg = `Concluir ${comuns.length} tarefa(s) comum(ns) selecionada(s)?`;
        if (excluir.length) msg += `\n\n${excluir.length} reunião(ões)/treinamento(s) serão ignorados — conclua-os individualmente pelo botão de concluir.`;
        if (!window.confirm(msg)) return;
        setBusy(true);
        try { await api.post(`/tasks/bulk-complete`, { task_ids: comuns, observacao: "Concluído em lote pela Agenda" }); setSelected(new Set()); await onChanged(); }
        finally { setBusy(false); }
    }
    async function bulkExcluir() {
        if (selected.size === 0) return;
        if (!window.confirm(`Excluir permanentemente ${selected.size} tarefa(s)?`)) return;
        setBusy(true);
        try { await api.post(`/tasks/bulk-delete`, { task_ids: Array.from(selected) }); setSelected(new Set()); await onChanged(); }
        finally { setBusy(false); }
    }

    return (
        <section className="bg-white border border-slate-200 rounded-lg p-6" data-testid="agenda-panel">
            <div className="flex items-center gap-2 mb-3">
                <Calendar className="h-4 w-4 text-[#0F2044]" />
                <h3 className="font-display font-semibold text-base text-[#0F2044]">Agenda dos próximos 7 dias</h3>
                {selected.size > 0 && (
                    <div className="ml-auto flex items-center gap-2" data-testid="agenda-bulk-bar">
                        <span className="text-xs text-slate-500">{selected.size} selecionada(s)</span>
                        <button onClick={bulkConcluir} disabled={busy} data-testid="agenda-bulk-complete"
                            className="text-xs font-semibold px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1 disabled:opacity-60">
                            <CheckSquare className="h-3.5 w-3.5" /> Concluir selecionadas
                        </button>
                        <button onClick={bulkExcluir} disabled={busy} data-testid="agenda-bulk-delete"
                            className="text-xs font-semibold px-3 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white inline-flex items-center gap-1 disabled:opacity-60">
                            <Trash2 className="h-3.5 w-3.5" /> Excluir selecionadas
                        </button>
                    </div>
                )}
            </div>
            {rows.length === 0 ? (
                <div className="text-sm text-slate-500 py-3">Sem tarefas previstas para os próximos 7 dias.</div>
            ) : (
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs uppercase tracking-[0.12em] text-slate-500 border-b border-slate-100">
                            <th className="py-2 pr-2 w-8">
                                <input type="checkbox" checked={selected.size === rows.length && rows.length > 0}
                                    onChange={toggleAll} data-testid="agenda-select-all"
                                    className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                            </th>
                            <th className="py-2 pr-3 font-medium">Cliente</th>
                            <th className="py-2 pr-3 font-medium">Tarefa</th>
                            <th className="py-2 pr-3 font-medium">Responsável</th>
                            <th className="py-2 pr-3 font-medium">Prazo</th>
                            <th className="py-2 font-medium text-right">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((a, i) => (
                            <tr key={a.task_id || i} className="border-b border-slate-50 hover:bg-slate-50 group" data-testid={`agenda-row-${i}`}>
                                <td className="py-2 pr-2">
                                    <input type="checkbox" checked={selected.has(a.task_id)} onChange={() => toggle(a.task_id)}
                                        data-testid={`agenda-select-${i}`}
                                        className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                                </td>
                                <td className="py-2 pr-3">
                                    <Link to={`/contratos/${a.contract_id}`} className="text-[#0F2044] hover:underline font-medium">
                                        {a.organizacao_nome}
                                    </Link>
                                </td>
                                <td className="py-2 pr-3 text-slate-700">{a.descricao}</td>
                                <td className="py-2 pr-3 text-slate-600">{a.responsavel}</td>
                                <td className="py-2 pr-3 font-mono-tab">
                                    <span className={a.atrasada ? "text-red-600 font-semibold" : "text-slate-700"}>{brl(a.predicted_date)}</span>
                                </td>
                                <td className="py-2 text-right whitespace-nowrap">
                                    <button onClick={() => concluir(a)} disabled={busy} data-testid={`agenda-complete-${i}`}
                                        title="Concluir" className="p-1.5 rounded text-emerald-600 hover:bg-emerald-50 disabled:opacity-50">
                                        <CheckCircle2 className="h-4 w-4" />
                                    </button>
                                    <button onClick={() => setRemanejando({ task: a, date: a.predicted_date })} data-testid={`agenda-reschedule-${i}`}
                                        title="Remanejar data" className="p-1.5 rounded text-slate-500 hover:text-[#0F2044] hover:bg-slate-100">
                                        <Clock className="h-4 w-4" />
                                    </button>
                                    <button onClick={() => setEditing({ ...a })} data-testid={`agenda-edit-${i}`}
                                        title="Editar" className="p-1.5 rounded text-slate-500 hover:text-[#0F2044] hover:bg-slate-100">
                                        <Pencil className="h-4 w-4" />
                                    </button>
                                    <button onClick={() => excluir(a)} disabled={busy} data-testid={`agenda-delete-${i}`}
                                        title="Excluir" className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50">
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {remanejando && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4" data-testid="agenda-reschedule-modal" onClick={() => setRemanejando(null)}>
                    <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6 fade-in" onClick={(e) => e.stopPropagation()}>
                        <h3 className="font-display font-semibold text-lg text-[#0F2044] mb-1">Remanejar data</h3>
                        <p className="text-xs text-slate-500 mb-4">{remanejando.task.descricao}</p>
                        <input type="date" value={remanejando.date || ""} onChange={(e) => setRemanejando({ ...remanejando, date: e.target.value })}
                            data-testid="agenda-reschedule-date" className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm" />
                        <div className="flex justify-end gap-2 mt-4">
                            <button onClick={() => setRemanejando(null)} className="text-sm px-4 py-2 border border-slate-300 rounded-md hover:bg-slate-50">Cancelar</button>
                            <button onClick={salvarRemanejo} disabled={busy} data-testid="agenda-reschedule-save"
                                className="text-sm px-4 py-2 bg-[#0F2044] hover:bg-[#172E5E] text-white rounded-md font-semibold disabled:opacity-60">Salvar</button>
                        </div>
                    </div>
                </div>
            )}

            {editing && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4" data-testid="agenda-edit-modal" onClick={() => setEditing(null)}>
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 fade-in" onClick={(e) => e.stopPropagation()}>
                        <h3 className="font-display font-semibold text-lg text-[#0F2044] mb-4">Editar tarefa</h3>
                        <label className="block text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1">Descrição</label>
                        <textarea rows={2} value={editing.descricao} onChange={(e) => setEditing({ ...editing, descricao: e.target.value })}
                            data-testid="agenda-edit-descricao" className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm mb-3" />
                        <label className="block text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1">Responsável</label>
                        <input value={editing.responsavel || ""} onChange={(e) => setEditing({ ...editing, responsavel: e.target.value })}
                            data-testid="agenda-edit-responsavel" className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm" />
                        <div className="flex justify-end gap-2 mt-4">
                            <button onClick={() => setEditing(null)} className="text-sm px-4 py-2 border border-slate-300 rounded-md hover:bg-slate-50">Cancelar</button>
                            <button onClick={salvarEdicao} disabled={busy} data-testid="agenda-edit-save"
                                className="text-sm px-4 py-2 bg-[#0F2044] hover:bg-[#172E5E] text-white rounded-md font-semibold disabled:opacity-60">Salvar</button>
                        </div>
                    </div>
                </div>
            )}

            {activityModal?.type === "reuniao" && (
                <CompleteMeetingTaskModal contract={activityModal.contract} task={activityModal.task}
                    onClose={() => setActivityModal(null)}
                    onSaved={async () => { setActivityModal(null); await onChanged(); }} />
            )}
            {activityModal?.type === "treinamento" && (
                <CompleteTreinamentoTaskModal contract={activityModal.contract} task={activityModal.task}
                    onClose={() => setActivityModal(null)}
                    onSaved={async () => { setActivityModal(null); await onChanged(); }} />
            )}
        </section>
    );
}

function Movers({ items, positive }) {
    if (!items || items.length === 0) return <div className="text-sm text-slate-500">Sem dados suficientes.</div>;
    return (
        <ul className="divide-y divide-slate-100">
            {items.map((c, i) => (
                <li key={c.contract_id} className="py-2 flex items-center justify-between" data-testid={`mover-${positive ? "growth" : "decline"}-${i}`}>
                    <Link to={`/contratos/${c.contract_id}`} className="text-sm text-[#0F2044] hover:underline font-medium">
                        {c.organizacao_nome}
                    </Link>
                    <span className={`font-mono-tab text-sm font-semibold ${c.variacao >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                        {fmtPct(c.variacao, { signed: true })}
                    </span>
                </li>
            ))}
        </ul>
    );
}

function ClickableKpi({ icon: Icon, color, label, value, hint, onClick, testid }) {
    return (
        <button onClick={onClick} data-testid={testid}
            className="text-left bg-white border border-slate-200 rounded-lg p-4 hover:border-slate-300 hover:shadow-sm transition-all group">
            <div className="flex items-start justify-between">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">{label}</div>
                    <div className="mt-1.5 text-3xl font-display font-bold" style={{ color }}>{value}</div>
                    <div className="text-[11px] text-slate-500 mt-1">{hint}</div>
                </div>
                <div className="h-9 w-9 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: color + "15" }}>
                    <Icon className="h-4 w-4" style={{ color }} />
                </div>
            </div>
            <div className="mt-2 text-[10px] text-slate-400 group-hover:text-[#0F2044] inline-flex items-center gap-1 transition-colors">
                Ver lista <ArrowRight className="h-2.5 w-2.5" />
            </div>
        </button>
    );
}

// M4 — modal Clientes a contatar: sem reunião/treinamento no prazo do plano (45d premium, 30d personalizado)
function AContatarModal({ items, onClose, onRegistered }) {
    const [registrando, setRegistrando] = useState(null); // item p/ registrar contato
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4" data-testid="a-contatar-modal" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full p-6 fade-in max-h-[82vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Sem reunião/treinamento no prazo do plano</div>
                        <h3 className="font-display font-semibold text-lg text-[#0F2044] mt-1">Clientes a contatar ({items.length})</h3>
                        <div className="text-xs text-slate-500 mt-0.5">Regra: 45 dias (Premium) · 30 dias (Personalizado) — a contagem reinicia a cada novo registro</div>
                    </div>
                    <button onClick={onClose} data-testid="a-contatar-close" className="text-slate-400 hover:text-slate-700 -mt-1">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                {items.length === 0 ? (
                    <div className="text-sm text-slate-500 py-6 text-center">Todos os clientes foram contatados dentro do prazo. 🎉</div>
                ) : (
                    <div className="overflow-y-auto -mx-2 px-2">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 border-b border-slate-200">
                                    <th className="py-2 pr-3 font-medium">Cliente</th>
                                    <th className="py-2 pr-3 font-medium">Plano</th>
                                    <th className="py-2 pr-3 font-medium">Último contato</th>
                                    <th className="py-2 pr-3 font-medium">Tipo</th>
                                    <th className="py-2 pr-3 font-medium text-right">Dias sem contato</th>
                                    <th className="py-2 font-medium text-right">Ação</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((it, i) => (
                                    <tr key={it.contract_id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`a-contatar-row-${i}`}>
                                        <td className="py-2.5 pr-3">
                                            <Link to={`/contratos/${it.contract_id}`} className="font-medium text-[#0F2044] hover:underline">
                                                {it.organizacao_nome}
                                            </Link>
                                        </td>
                                        <td className="py-2.5 pr-3 capitalize text-slate-600">{it.plano}</td>
                                        <td className="py-2.5 pr-3 font-mono-tab text-slate-600">{it.ultimo_contato ? brl(it.ultimo_contato) : "—"}</td>
                                        <td className="py-2.5 pr-3 text-slate-600">{it.tipo_ultimo_contato}</td>
                                        <td className="py-2.5 pr-3 text-right">
                                            <span className={`font-mono-tab font-semibold ${it.dias_sem_contato >= it.limite_dias * 2 ? "text-red-600" : "text-amber-600"}`}>
                                                {it.dias_sem_contato}d
                                            </span>
                                            <span className="text-[10px] text-slate-400 ml-1">/ {it.limite_dias}d</span>
                                        </td>
                                        <td className="py-2.5 text-right">
                                            <button onClick={() => setRegistrando(it)} data-testid={`a-contatar-concluir-${i}`}
                                                className="text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-md inline-flex items-center gap-1.5">
                                                <CheckCircle2 className="h-3.5 w-3.5" /> Concluir contato
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            {registrando && (
                <ContatoModal item={registrando}
                    onClose={() => setRegistrando(null)}
                    onSaved={async () => { setRegistrando(null); if (onRegistered) await onRegistered(); onClose(); }} />
            )}
        </div>
    );
}

// M3 — modal de registro de contato realizado (Dashboard → Histórico + Jornada)
function ContatoModal({ item, onClose, onSaved }) {
    const [form, setForm] = useState({
        forma: "ligacao",
        data: new Date().toISOString().slice(0, 10),
        responsavel: CONTATO_RESPONSAVEIS[0],
        titulo: "",
        descricao: "",
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    async function submit() {
        if (!form.titulo.trim()) { setError("Informe o título do contato."); return; }
        if (!form.data) { setError("Informe a data do contato."); return; }
        setSaving(true); setError("");
        try {
            await api.post(`/contracts/${item.contract_id}/contatos`, { ...form, titulo: form.titulo.trim() });
            await onSaved();
        } catch (e) {
            setError(e?.response?.data?.detail || "Erro ao registrar o contato.");
            setSaving(false);
        }
    }

    const inputCls = "w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#0F2044] focus:ring-1 focus:ring-[#0F2044]/30 focus:outline-none";
    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 px-4 overflow-y-auto py-6" data-testid="contato-modal" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 fade-in my-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-600 font-semibold">Concluir contato</div>
                        <h3 className="font-display font-semibold text-lg text-[#0F2044] mt-1">{item.organizacao_nome}</h3>
                        <div className="text-xs text-slate-500 mt-1">O registro entra no Histórico e vira uma tarefa concluída na Jornada e Checklist, reiniciando a contagem de contato.</div>
                    </div>
                    <button onClick={onClose} data-testid="contato-close" className="text-slate-400 hover:text-slate-700 -mt-1">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1">Forma de contato *</label>
                            <select value={form.forma} onChange={(e) => setForm({ ...form, forma: e.target.value })}
                                data-testid="contato-forma" className={`${inputCls} bg-white`}>
                                {CONTATO_FORMAS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1">Data do contato *</label>
                            <input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })}
                                data-testid="contato-data" className={inputCls} />
                        </div>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1">Responsável *</label>
                        <select value={form.responsavel} onChange={(e) => setForm({ ...form, responsavel: e.target.value })}
                            data-testid="contato-responsavel" className={`${inputCls} bg-white`}>
                            {CONTATO_RESPONSAVEIS.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1">Título do contato *</label>
                        <input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                            placeholder="Ex.: Follow-up de acompanhamento mensal" data-testid="contato-titulo" className={inputCls} autoFocus />
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1">Descrição do contato realizado</label>
                        <textarea rows={3} value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                            data-testid="contato-descricao" className={inputCls} />
                    </div>
                    {error && <div className="text-xs text-red-600 font-medium" data-testid="contato-error">{error}</div>}
                </div>
                <div className="flex justify-end gap-2 mt-5">
                    <button onClick={onClose} data-testid="contato-cancel" className="text-sm px-4 py-2 border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50">Cancelar</button>
                    <button onClick={submit} disabled={saving} data-testid="contato-save"
                        className="text-sm px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md font-semibold disabled:opacity-60 inline-flex items-center gap-1.5">
                        <CheckCircle2 className="h-4 w-4" /> {saving ? "Registrando…" : "Registrar contato"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// M2 — modal com a lista de reuniões/treinamentos do mês, linkando para o contrato
function ListModal({ title, items, emptyText, onClose }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4" data-testid="dashboard-list-modal" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 fade-in max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Mês corrente</div>
                        <h3 className="font-display font-semibold text-lg text-[#0F2044] mt-1">{title} ({items.length})</h3>
                    </div>
                    <button onClick={onClose} data-testid="dashboard-list-modal-close" className="text-slate-400 hover:text-slate-700 -mt-1">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                {items.length === 0 ? (
                    <div className="text-sm text-slate-500 py-6 text-center">{emptyText}</div>
                ) : (
                    <ul className="divide-y divide-slate-100 overflow-y-auto -mx-2 px-2">
                        {items.map((it, i) => (
                            <li key={i} className="py-3 flex items-start gap-3" data-testid={`dashboard-list-item-${i}`}>
                                <div className="flex-1 min-w-0">
                                    <Link to={`/contratos/${it.contract_id}`} className="font-medium text-[#0F2044] hover:underline text-sm">
                                        {it.organizacao_nome}
                                    </Link>
                                    <div className="text-sm text-slate-700">{it.descricao}</div>
                                    <div className="text-xs text-slate-500 mt-0.5">
                                        {brl(it.data)} · <span className="capitalize">{it.origem}</span>
                                    </div>
                                </div>
                                <Link to={`/contratos/${it.contract_id}`} className="text-xs text-[#0F2044] hover:underline whitespace-nowrap flex items-center gap-1 mt-0.5">
                                    Acessar <ArrowRight className="h-3 w-3" />
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
